import { EventEmitter, Injectable } from '@angular/core';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import { PaymentsAnalyticsService } from '@payments/tracking/analytics/payments-analytics.service';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ServiceHandlerService } from '@shared/services/service-handler.service';
import {
  IsrDocFormat,
  ISRDocumentResponse,
  PaymentTransactionsViewModel,
} from '@usf/payment-types';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FileHandlerPaymentsService } from './file-handler-payments.service';
@Injectable({
  providedIn: 'root',
})
export class InvoiceIsrService {
  errorMessage = 'allInvoices.viewInvoiceError';
  constructor(
    readonly platformService: PlatformService,
    private serviceHandler: ServiceHandlerService,
    private loadingSpinnerService: LoadingSpinnerService,
    private translateService: TranslateService,
    private popoverController: PopoverController,
    private actionSheetController: ActionSheetController,
    private fileHandlerService: FileHandlerPaymentsService,
    private paymentsAnalyticsService: PaymentsAnalyticsService,
  ) {}

  fetchISRDocuments(
    documentIds: string[],
    documentFormat = IsrDocFormat.PDF,
  ): Observable<ISRDocumentResponse> {
    const body = {
      docIDs: documentIds,
      docFormat: documentFormat.toUpperCase(),
    };
    return this.serviceHandler
      .post<ISRDocumentResponse>(
        `${environment.paymentApiUrl}/isrDocument`,
        body,
      )
      .pipe(
        take(1),
        filter(res => !!res),
        catchError(error => throwError(() => error)),
      );
  }

  async openInvoiceOptions(
    {
      event,
      transaction,
    }: {
      event?: Event;
      transaction: PaymentTransactionsViewModel;
    },
    additionalButtons: any[] = [],
  ) {
    const buttons: any[] = [];

    if (!!transaction.isr && Object.keys(transaction.isr).length > 0) {
      buttons.push(
        {
          text: this.translateService.instant('i18n.common.view'),
          handler: () => {
            this.viewInvoice(
              transaction.isr.docID.toString(),
              transaction.transactionNumber,
              transaction.secondaryTransactionNumber,
            );
            this.dismissPopover();
          },
        },
        {
          text: this.translateService.instant('i18n.common.download'),
          handler: () => {
            this.mapInvoicesToShow([transaction]);
            this.dismissPopover();
          },
        },
      );
    }

    buttons.push(...additionalButtons);

    if (!this.platformService.isTouch.value) {
      const popover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        cssClass: 'ellipsis-options-popover-desktop',
        componentProps: {
          buttons,
        },
        mode: 'ios',
        event: event,
        showBackdrop: false,
      });
      return await popover.present();
    } else {
      buttons.push({
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
        handler: () => {},
      });

      const actionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons: buttons,
      });
      await actionSheet.present();
    }
  }

  async dismissPopover() {
    if (!this.platformService.isTouch.value) {
      await this.popoverController.dismiss();
    }
  }

  mapInvoicesToShow(transactions: PaymentTransactionsViewModel[]) {
    const transactionIds = transactions
      .map(transaction =>
        transaction.isr?.docID ? transaction.isr.docID.toString() : undefined,
      )
      .filter(transactionId => !!transactionId);
    this.showDownloadInvoicesModal(transactionIds);
  }

  async viewInvoice(
    transactionId: string,
    transactionNumber: string,
    secondaryTransactionNumber: string,
  ) {
    this.fetchAndDownloadInvoice(
      [transactionId],
      IsrDocFormat.PDF,
      true,
      `${transactionNumber}${secondaryTransactionNumber}`,
    );
  }

  async showDownloadInvoicesModal(transactionIds: string[]) {
    const isTouch = this.platformService.isTouch.value;
    const downloadInvoicesEvent$: EventEmitter<IsrDocFormat> =
      new EventEmitter();
    downloadInvoicesEvent$.subscribe(format => {
      this.fetchAndDownloadInvoice(transactionIds, format);
    });

    return this.fileHandlerService.chooseFormatDownloadModal(
      downloadInvoicesEvent$,
      [
        {
          format: IsrDocFormat.PDF,
          title: 'i18n.payments.invoiceDownload.pdfTitle',
          helpText: '',
        },
        {
          format: IsrDocFormat.CSV_FULL,
          title: 'i18n.payments.invoiceDownload.csvFullitle',
          helpText: 'i18n.payments.invoiceDownload.csvFullHelpText',
        },
        {
          format: IsrDocFormat.CSV_LINES,
          title: 'i18n.payments.invoiceDownload.csvLinesTitle',
          helpText: 'i18n.payments.invoiceDownload.csvLinesHelpText',
        },
        {
          format: IsrDocFormat.EXCELTAB,
          title: 'i18n.payments.invoiceDownload.excelTabTitle',
          helpText: 'i18n.payments.invoiceDownload.excelTabHelpText',
        },
        {
          format: IsrDocFormat.EXCELFLAT,
          title: 'i18n.payments.invoiceDownload.excelFlatTitle',
          helpText: 'i18n.payments.invoiceDownload.excelFlatHelpText',
        },
        {
          format: IsrDocFormat.QB_BILL,
          title: 'i18n.payments.invoiceDownload.quickBooksTitle',
          helpText: '',
        },
        {
          format: IsrDocFormat.XML,
          title: 'i18n.payments.invoiceDownload.xmlTitle',
          helpText: '',
        },
      ],
      isTouch,
    );
  }

  fetchAndDownloadInvoice(
    transactionIds: string[],
    docFormat: IsrDocFormat = IsrDocFormat.PDF,
    isView = false,
    fileNameOpt?: string,
  ) {
    this.loadingSpinnerService
      .createSpinnerModal(false, 'i18n.common.loading')
      .then(() => {
        this.fetchISRDocuments(transactionIds, docFormat).subscribe({
          next: async res => {
            this.loadingSpinnerService.dismissSpinnerModal();
            this.fileHandlerService.downloadAction(
              res,
              isView,
              isView ? 'invoiceDownload.unableToView' : this.errorMessage,
              fileNameOpt,
            );

            isView
              ? this.paymentsAnalyticsService.trackView()
              : this.paymentsAnalyticsService.trackDownload(
                  transactionIds.length,
                  res.docFormat,
                );
          },
          error: e => {
            this.loadingSpinnerService.dismissSpinnerModal();
            const errorCode =
              e.status === 400
                ? 'FPGN-1011'
                : e.status === 500
                  ? 'FPGN-1012'
                  : 'FPGN-1014';
            this.fileHandlerService.handleErrorMessage(
              new Error(errorCode),
              this.errorMessage,
            );
          },
        });
      });
  }

  hasSomeISR(transactions: PaymentTransactionsViewModel[]): boolean {
    return (
      transactions.filter(vm => Object.keys(vm.isr ?? {}).length).length > 0
    );
  }
}
